import { Grid } from '../core/Grid';
import { Color, ColorMode, TRANSPARENT, rgb } from '../core/types';
import { quantize } from '../ansi/Palette';
import { GLYPH_H, GLYPH_W, GlyphAtlas, GlyphFamily } from './glyphAtlas';
import { SourceImage, luminance } from './convert';

/**
 * Content-aware image conversion.
 *
 * Instead of deciding up front that the whole picture is "half blocks" or
 * "Braille", every cell is matched against a library of candidate glyphs and
 * the closest one wins. A region of fine scattered detail lands on Braille, a
 * horizontal rule lands on a box-drawing line of the right weight, a letter in
 * the source lands on that letter, and flat regions land on blocks or shades —
 * because those are genuinely the closest matches to what is there.
 *
 * Each cell also gets two colours: the average of its "ink" pixels and the
 * average of its "paper" pixels, which is exactly what a terminal cell can
 * show.
 */

export interface SmartOptions {
  width: number;
  height: number;
  colorMode: ColorMode;
  /** Bias toward simpler glyphs; stops flat areas turning into letter soup. */
  simplicity: number;
  brightness: number;
  contrast: number;
  /** Cells with less contrast than this are treated as flat fills. */
  flatThreshold: number;
  /** Alpha below this leaves the cell empty. */
  alphaThreshold: number;
  /** Paint the background colour as well as the foreground. */
  paintBackground: boolean;
  invert: boolean;
}

export const DEFAULT_SMART: SmartOptions = {
  width: 80,
  height: 24,
  colorMode: 'truecolor',
  simplicity: 0.35,
  brightness: 0,
  contrast: 0,
  flatThreshold: 0.06,
  alphaThreshold: 24,
  paintBackground: true,
  invert: false,
};

/** Scales the complexity penalty into the same units as the mean squared
 *  reconstruction error, so the simplicity slider reads 0..2 either way. */
const COMPLEXITY_SCALE = 0.02;

export const DEFAULT_SMART_FAMILIES: GlyphFamily[] = ['blocks', 'shades', 'box', 'braille', 'letters'];

export interface SmartResult {
  grid: Grid;
  /** How many cells each family won, for the report in the UI. */
  usage: Record<string, number>;
}

interface CellSamples {
  /** GLYPH_W * GLYPH_H luminance values, 0..1. */
  lum: Float32Array;
  /** Matching RGB sums per feature cell. */
  r: Float32Array;
  g: Float32Array;
  b: Float32Array;
  alpha: number;
  min: number;
  max: number;
}

/** Averages the source pixels of one cell into the feature grid. */
function sampleCell(
  src: SourceImage,
  x0: number,
  y0: number,
  cellW: number,
  cellH: number,
  opts: SmartOptions,
): CellSamples {
  const n = GLYPH_W * GLYPH_H;
  const lum = new Float32Array(n);
  const r = new Float32Array(n);
  const g = new Float32Array(n);
  const b = new Float32Array(n);
  const counts = new Float32Array(n);
  let alphaSum = 0;
  let pixels = 0;

  const contrast = (opts.contrast + 100) / 100;
  const brightness = opts.brightness * 2.55;

  /*
   * Iterate over the feature grid and pull pixels for each feature, rather
   * than scattering pixels into features.
   *
   * A cell is often smaller than the 6x12 feature grid — 40 columns across a
   * 160px image gives 4x8 pixels per cell — and scattering left most features
   * with no samples at all. Those defaulted to black, which invented a
   * high-contrast dot pattern out of nothing: that is why photographs came out
   * as Braille, and far too dark. Sampling per feature, always over at least
   * one pixel, means every feature has a real colour.
   */
  for (let fy = 0; fy < GLYPH_H; fy++) {
    const ty0 = y0 + (fy * cellH) / GLYPH_H;
    const ty1 = y0 + ((fy + 1) * cellH) / GLYPH_H;
    let sy0 = Math.floor(ty0);
    let sy1 = Math.ceil(ty1);
    if (sy1 <= sy0) sy1 = sy0 + 1;

    for (let fx = 0; fx < GLYPH_W; fx++) {
      const tx0 = x0 + (fx * cellW) / GLYPH_W;
      const tx1 = x0 + ((fx + 1) * cellW) / GLYPH_W;
      let sx0 = Math.floor(tx0);
      let sx1 = Math.ceil(tx1);
      if (sx1 <= sx0) sx1 = sx0 + 1;

      const fi = fy * GLYPH_W + fx;
      for (let sy = sy0; sy < sy1; sy++) {
        const cy = Math.max(0, Math.min(src.height - 1, sy));
        for (let sx = sx0; sx < sx1; sx++) {
          const cx = Math.max(0, Math.min(src.width - 1, sx));
          const i = (cy * src.width + cx) * 4;
          const cr = clamp8((src.data[i] - 128) * contrast + 128 + brightness);
          const cg = clamp8((src.data[i + 1] - 128) * contrast + 128 + brightness);
          const cb = clamp8((src.data[i + 2] - 128) * contrast + 128 + brightness);
          lum[fi] += luminance(cr, cg, cb);
          r[fi] += cr;
          g[fi] += cg;
          b[fi] += cb;
          counts[fi] += 1;
          alphaSum += src.data[i + 3];
          pixels += 1;
        }
      }
    }
  }

  let min = Infinity;
  let max = -Infinity;
  for (let i = 0; i < n; i++) {
    const c = counts[i] || 1;
    lum[i] = lum[i] / c / 255;
    if (opts.invert) lum[i] = 1 - lum[i];
    r[i] /= c;
    g[i] /= c;
    b[i] /= c;
    if (opts.invert) {
      r[i] = 255 - r[i];
      g[i] = 255 - g[i];
      b[i] = 255 - b[i];
    }
    if (lum[i] < min) min = lum[i];
    if (lum[i] > max) max = lum[i];
  }

  return { lum, r, g, b, alpha: pixels ? alphaSum / pixels : 0, min, max };
}

function clamp8(v: number): number {
  return v < 0 ? 0 : v > 255 ? 255 : v;
}

/**
 * Splits a cell's luminance into two levels with a two-means pass.
 *
 * Returns the two levels and the share of the cell that belongs to the bright
 * one. This replaces the old contrast-stretch: normalising every cell to the
 * full 0..1 range made a smooth photographic gradient look like a
 * high-contrast pattern, which is why Braille — the family with the most
 * fine-grained patterns — used to win almost everywhere.
 */
function twoLevels(lum: Float32Array): { lo: number; hi: number; brightShare: number } {
  let lo = Infinity;
  let hi = -Infinity;
  for (const v of lum) {
    if (v < lo) lo = v;
    if (v > hi) hi = v;
  }
  // Three iterations is plenty for 72 samples and one dimension.
  for (let pass = 0; pass < 3; pass++) {
    const mid = (lo + hi) / 2;
    let loSum = 0;
    let loCount = 0;
    let hiSum = 0;
    let hiCount = 0;
    for (const v of lum) {
      if (v < mid) {
        loSum += v;
        loCount += 1;
      } else {
        hiSum += v;
        hiCount += 1;
      }
    }
    if (loCount === 0 || hiCount === 0) break;
    lo = loSum / loCount;
    hi = hiSum / hiCount;
  }

  const mid = (lo + hi) / 2;
  let bright = 0;
  for (const v of lum) if (v >= mid) bright += 1;
  return { lo, hi, brightShare: bright / lum.length };
}

/**
 * Reconstruction error for one glyph at one ink/paper assignment.
 *
 * A glyph paints `coverage` of ink over paper, so the luminance it predicts is
 * `paper + coverage * (ink - paper)`. Scoring by how far that lands from the
 * cell's real luminance is what makes the choice faithful: a photographic cell
 * is reproduced best by whichever glyph splits it the way the image actually
 * splits, and a flat-ish cell has nothing to gain from a busy glyph.
 */
function reconstructionError(
  lum: Float32Array,
  coverage: Float32Array,
  ink: number,
  paper: number,
  ceiling: number,
): number {
  const delta = ink - paper;
  let error = 0;
  for (let i = 0; i < lum.length; i++) {
    const predicted = paper + coverage[i] * delta;
    const d = lum[i] - predicted;
    error += d * d;
    if (error >= ceiling) return Infinity;
  }
  return error / lum.length;
}

function averageColor(
  cell: CellSamples,
  pattern: Float32Array,
  wantInk: boolean,
  mode: ColorMode,
): Color | null {
  let r = 0;
  let g = 0;
  let b = 0;
  let weight = 0;
  for (let i = 0; i < pattern.length; i++) {
    const w = wantInk ? pattern[i] : 1 - pattern[i];
    if (w <= 0.02) continue;
    r += cell.r[i] * w;
    g += cell.g[i] * w;
    b += cell.b[i] * w;
    weight += w;
  }
  if (weight <= 0.05) return null;
  return quantize(rgb(Math.round(r / weight), Math.round(g / weight), Math.round(b / weight)), mode);
}

/** Which family a matched character belongs to, for the usage report. */
export function familyOf(char: string): string {
  const cp = char.codePointAt(0) ?? 0;
  if (cp >= 0x2800 && cp <= 0x28ff) return 'braille';
  if (cp >= 0x2500 && cp <= 0x257f) return 'box';
  if (cp >= 0x2591 && cp <= 0x2593) return 'shades';
  if (cp >= 0x2580 && cp <= 0x259f) return 'blocks';
  if (char === ' ') return 'space';
  if (/[A-Za-z0-9]/.test(char)) return 'letters';
  return 'punctuation';
}

/**
 * Matches every cell of an image against the atlas.
 *
 * The search is a sum of squared differences over the feature grid, with two
 * adjustments that matter in practice: a penalty proportional to glyph
 * complexity (so a flat patch prefers a block over a letter that happens to
 * fit), and a mean-coverage pre-filter that skips candidates too light or too
 * dark to win.
 */
export function smartConvert(
  src: SourceImage,
  atlas: GlyphAtlas,
  options: Partial<SmartOptions> = {},
): SmartResult {
  const opts: SmartOptions = { ...DEFAULT_SMART, ...options };
  const cols = Math.max(1, Math.round(opts.width));
  const rows = Math.max(1, Math.round(opts.height));
  const grid = new Grid(cols, rows);
  const usage: Record<string, number> = {};

  const cellW = src.width / cols;
  const cellH = src.height / rows;
  const n = GLYPH_W * GLYPH_H;

  for (let cy = 0; cy < rows; cy++) {
    for (let cx = 0; cx < cols; cx++) {
      const cell = sampleCell(src, cx * cellW, cy * cellH, cellW, cellH, opts);
      if (cell.alpha < opts.alphaThreshold) continue;

      const flat = cell.max - cell.min < opts.flatThreshold;
      if (flat) {
        // Nothing to draw: one solid colour fills the cell.
        const color = averageColor(cell, new Float32Array(n).fill(1), true, opts.colorMode);
        if (color === null) continue;
        grid.set(cx, cy, {
          char: ' ',
          fg: TRANSPARENT,
          bg: opts.paintBackground ? color : TRANSPARENT,
          attrs: 0,
        });
        usage.space = (usage.space ?? 0) + 1;
        continue;
      }

      const { lo, hi, brightShare } = twoLevels(cell.lum);

      /*
       * Both orientations are tried: a glyph can be bright ink on dark paper
       * or the reverse, and outside the symmetric block family (there is no
       * inverse of the letter A) only one of them will fit.
       */
      let best: (typeof atlas.glyphs)[number] | null = null;
      let bestScore = Infinity;

      for (const inkIsBright of [true, false]) {
        const ink = inkIsBright ? hi : lo;
        const paper = inkIsBright ? lo : hi;
        const inkShare = inkIsBright ? brightShare : 1 - brightShare;
        for (const glyph of atlas.glyphs) {
          // Coverage far from the cell's ink share cannot win; skip it.
          if (Math.abs(glyph.mean - inkShare) > 0.34) continue;
          const penalty = glyph.complexity * opts.simplicity * COMPLEXITY_SCALE;
          if (penalty >= bestScore) continue;
          const error = reconstructionError(
            cell.lum,
            glyph.coverage,
            ink,
            paper,
            (bestScore - penalty) * n,
          );
          const score = error + penalty;
          if (score < bestScore) {
            bestScore = score;
            best = glyph;
          }
        }
      }
      if (!best) continue;

      // Foreground is the ink side of the glyph, background the paper side.
      const fg = averageColor(cell, best.coverage, true, opts.colorMode);
      const bg = averageColor(cell, best.coverage, false, opts.colorMode);

      // When both sides quantise to the same colour the glyph draws nothing
      // visible, so a plain filled cell says the same thing in fewer bytes.
      if (fg !== null && fg === bg && opts.paintBackground) {
        grid.set(cx, cy, { char: ' ', fg: TRANSPARENT, bg: fg, attrs: 0 });
        usage.space = (usage.space ?? 0) + 1;
        continue;
      }

      grid.set(cx, cy, {
        char: best.char,
        fg: fg ?? TRANSPARENT,
        bg: opts.paintBackground ? bg ?? TRANSPARENT : TRANSPARENT,
        attrs: 0,
      });
      const family = familyOf(best.char);
      usage[family] = (usage[family] ?? 0) + 1;
    }
  }

  return { grid, usage };
}
